import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Link2, Link2Off, Loader2, CheckCircle2, AlertTriangle, XCircle,
    RefreshCw, ArrowRight, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ConfirmModal from "./ConfirmModal";

const STATUS_META = {
    linked:  { Icon: CheckCircle2,  color: "text-success-c", bg: "bg-success/10", border: "border-success/30", label: "Підключено" },
    pending: { Icon: AlertTriangle, color: "text-warning-c", bg: "bg-warning/10", border: "border-warning/30", label: "Очікує прийняття" },
    error:   { Icon: XCircle,       color: "text-error-c",   bg: "bg-error/10",   border: "border-error/30",   label: "Помилка" },
};

const fmtDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("uk-UA", {
            day: "numeric", month: "long", year: "numeric",
        });
    } catch { return null; }
};

const GoogleAdsSection = () => {
    const navigate = useNavigate();
    const [account, setAccount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checking, setChecking] = useState(false);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [confirmReconnect, setConfirmReconnect] = useState(false);
    const [reconnecting, setReconnecting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/google-ads");
            setAccount(r.data && Object.keys(r.data).length ? r.data : null);
        } catch {
            setAccount(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleCheck = async () => {
        setChecking(true);
        try {
            const r = await api.post("/google-ads/check-link");
            if (r.data?.status === "linked") {
                toast.success("MCC-зв'язок активний!");
            } else if (r.data?.status === "pending") {
                toast.info("Запрошення ще не прийнято. Перевірте Google Ads.");
            } else {
                toast.warning(r.data?.message || "Не вдалося підтвердити зв'язок");
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Помилка перевірки");
        } finally {
            setChecking(false);
        }
    };

    const handleReconnect = async () => {
        // Reconnect = disconnect current + redirect to onboarding for a fresh customer_id input.
        // Backend's DELETE /google-ads also sends admin TG so operator can unlink in MCC.
        setReconnecting(true);
        try {
            await api.delete("/google-ads");
            toast.success("Поточний акаунт відключено. Введіть новий Customer ID.");
            navigate("/onboarding/step-1");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Не вдалося розпочати перепідключення");
            setReconnecting(false);
        }
        // Note: no `finally` reset — we're navigating away, component unmounts.
    };

    const handleDisconnect = async () => {
        setDisconnecting(true);
        try {
            await api.delete("/google-ads");
            toast.success("Google Ads відключено. Історичні дані збережено.");
            setConfirmDisconnect(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Не вдалося відключити");
        } finally {
            setDisconnecting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 py-6 text-sm text-faint-c" data-testid="googleads-loading">
                <Loader2 size={14} className="animate-spin" />
                Завантаження…
            </div>
        );
    }

    if (!account || !account.customer_id) {
        return (
            <div className="space-y-4" data-testid="googleads-not-connected">
                <div className="rounded-lg border border-border bg-surface-hover/20 p-4">
                    <div className="flex items-start gap-3">
                        <Link2Off size={18} className="text-faint-c mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <div className="font-semibold text-sm text-text-primary">Не підключено</div>
                            <p className="mt-1 text-xs text-secondary-c">
                                Підключіть Google Ads щоб отримувати моніторинг кампаній,
                                сповіщення про аномалії та автоматичний аналіз.
                            </p>
                            <button
                                type="button"
                                onClick={() => navigate("/onboarding/step-1")}
                                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                                           bg-primary hover:bg-primary/90 text-white text-xs font-semibold"
                                data-testid="googleads-connect"
                            >
                                <Link2 size={12} />
                                <span>Підключити Google Ads</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const status = account.status || "pending";
    const meta = STATUS_META[status] || STATUS_META.pending;
    const linkedDate = fmtDate(account.linked_at || account.created_at);

    return (
        <div className="space-y-4" data-testid="googleads-connected">
            <div className={`rounded-lg border ${meta.border} ${meta.bg} p-4`}>
                <div className="flex items-start gap-3">
                    <meta.Icon size={18} className={`${meta.color} mt-0.5 shrink-0`} />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm font-semibold ${meta.color}`} data-testid="googleads-status-label">
                                {meta.label}
                            </span>
                            <span className="text-xs font-mono text-text-primary px-2 py-0.5 rounded
                                             bg-surface-hover/40 border border-border"
                                  data-testid="googleads-customer-id">
                                {account.customer_id}
                            </span>
                        </div>
                        {linkedDate && status === "linked" && (
                            <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-secondary-c">
                                <Calendar size={11} />
                                <span>Підключено: {linkedDate}</span>
                            </div>
                        )}
                        {status === "pending" && (
                            <p className="mt-2 text-xs text-secondary-c">
                                Запрошення надіслано. Прийміть його у Google Ads (Інструменти → Налаштування доступу).
                            </p>
                        )}
                        {status === "error" && account.error_message && (
                            <p className="mt-2 text-xs text-error-c">{account.error_message}</p>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                {status === "pending" && (
                    <button
                        type="button"
                        onClick={handleCheck}
                        disabled={checking}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md
                                   bg-primary/15 hover:bg-primary/25 border border-primary/30
                                   text-primary-c text-xs font-semibold transition-colors
                                   disabled:opacity-60"
                        data-testid="googleads-check"
                    >
                        {checking ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                        <span>Перевірити статус</span>
                    </button>
                )}

                <button
                    type="button"
                    onClick={() => setConfirmReconnect(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md
                               bg-surface-hover/40 hover:bg-surface-hover/60 border border-border
                               text-text-primary text-xs font-semibold transition-colors"
                    data-testid="googleads-reconnect"
                >
                    <span>Перепідключити інший акаунт</span>
                    <ArrowRight size={12} />
                </button>

                <button
                    type="button"
                    onClick={() => setConfirmDisconnect(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md
                               border border-error/30 text-error-c
                               hover:bg-error/10 text-xs font-semibold transition-colors"
                    data-testid="googleads-disconnect"
                >
                    <Link2Off size={12} />
                    <span>Відключити</span>
                </button>
            </div>

            <div className="text-[11px] text-faint-c leading-relaxed border-t border-border/60 pt-3">
                <span className="font-mono">ℹ️</span> Історичні дані (стратегії, звіти, snapshots) зберігаються
                навіть після відключення. Ви можете повторно підключити Google Ads у будь-який момент.
            </div>

            <ConfirmModal
                open={confirmDisconnect}
                onClose={() => setConfirmDisconnect(false)}
                onConfirm={handleDisconnect}
                loading={disconnecting}
                danger
                title="Відключити Google Ads?"
                body={
                    <>
                        Звʼязок з акаунтом <strong className="font-mono">{account.customer_id}</strong> буде
                        видалено, моніторинг та live-аналіз зупиняться.
                        <br /><br />
                        <span className="text-success-c font-semibold">Усі історичні дані зберігаються</span>
                        {" "}— стратегії, звіти, snapshot'и залишаться доступними.
                    </>
                }
                confirmLabel="Так, відключити"
                cancelLabel="Залишити"
                testidPrefix="googleads-disconnect-modal"
            />

            <ConfirmModal
                open={confirmReconnect}
                onClose={() => setConfirmReconnect(false)}
                onConfirm={handleReconnect}
                loading={reconnecting}
                title="Перепідключити інший акаунт?"
                body={
                    <>
                        Поточний акаунт <strong className="font-mono">{account.customer_id}</strong> буде
                        відключено, після чого вас перенаправить в онбординг для введення нового Customer ID.
                        <br /><br />
                        <span className="text-success-c font-semibold">Усі історичні дані зберігаються.</span>
                        {" "}Адмін отримає Telegram-нотіфікацію для ручного unlink у MCC.
                    </>
                }
                confirmLabel="Так, перепідключити"
                cancelLabel="Скасувати"
                testidPrefix="googleads-reconnect-modal"
            />
        </div>
    );
};

export default GoogleAdsSection;